// --- src/data/m3/Unit9.js ---

export const M3_UNIT_9 = {
  id: "m3-u9-music-culture",
  title: "Unit 9: Music & Culture",
  topic: "Music vocabulary, preferences, routines, and conversations from around the world",
  iconType: "music",
  grammarImage: "/m3-u9-music.jpg",
  grammarText: "Talk about music preferences, streaming habits, genres, concerts, and daily music routines. Use present simple, frequency, and likes/dislikes in musical contexts.",
  sets: [

    // ===================================================================
    // SET 1: MUSIC VOCABULARY BASICS (A2)
    // ===================================================================
    {
      id: "m3-u9-set1",
      title: "Set 1: Music Vocabulary 🎵",
      context: `*Lunes - Mumbai, India*

Maya es DJ en Mumbai. Hoy enseña vocabulario musical a estudiantes.
"Una canción es 'song'. Un grupo de músicos es 'band'. Un concierto es 'concert'."
Enseña las palabras básicas de música.

*Monday - Mumbai, India*

Maya is a DJ in Mumbai. Today she teaches music vocabulary to students.
"A song is 'song'. A group of musicians is 'band'. A concert is 'concert'."
She teaches basic music words.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match music term to meaning.",
          pairs: [
            { left: "song 🎵", right: "one piece of music" },
            { left: "band 🎸", right: "group of musicians" },
            { left: "album 💿", right: "collection of songs" }
          ],
          a: "completed",
          tip: "Basic music words!",
          successTip: "Music basics! Song = individual, album = collection, band = group! 🎵"
        },
        {
          type: "mc",
          q: "Maya plays music for people at a club. She is a:",
          options: ["DJ", "singer", "dancer"],
          a: "DJ",
          tip: "DJ = plays recorded music!",
          successTip: "DJ! Disk Jockey = person who plays music at clubs/parties! 🎧"
        },
        {
          type: "mc",
          q: "Live music performance = ___",
          options: ["concert", "album", "song"],
          a: "concert",
          tip: "Concert = live performance!",
          successTip: "Concert! Live music event where you see musicians perform! 🎤"
        },
        {
          type: "match",
          q: "Match music action to verb.",
          pairs: [
            { left: "enjoy music", right: "listen to" },
            { left: "create music", right: "write/compose" },
            { left: "perform music", right: "play/sing" }
          ],
          a: "completed",
          tip: "Music verbs!",
          successTip: "Music actions! Listen, write, play = basic music verbs! 🎶"
        },
        {
          type: "input",
          q: "A person who writes songs = ___",
          a: "songwriter",
          alts: ["composer", "writer"],
          tip: "Song + writer!",
          successTip: "Songwriter! Person who creates songs! Also 'composer'! ✍️🎵"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Me encanta escuchar música.'",
          words: ["I", "love", "listening", "to", "music."],
          a: "I love listening to music.",
          extra: ["listen", "the", "a"],
          tip: "Listen TO music!"
        },
        {
          type: "mc",
          q: "The main singer in a band:",
          options: ["lead singer", "main player", "top musician"],
          a: "lead singer",
          tip: "Lead = principal!",
          successTip: "Lead singer! Also 'lead vocalist' or 'frontman/frontwoman'! 🎤"
        },
        {
          type: "input",
          q: "Collection of songs released together = ___",
          a: "album",
          alts: ["record"],
          tip: "Multiple songs together!",
          successTip: "Album! Collection of 10-15 songs usually! 💿"
        },
        {
          type: "order",
          q: "Traduce: 'Esa banda toca rock.'",
          words: ["That", "band", "plays", "rock", "music."],
          a: "That band plays rock music.",
          extra: ["play", "the", "a"],
          tip: "Band = singular, plays!"
        },
        {
          type: "input",
          q: "One song from an album = a ___",
          a: "song",
          alts: ["track", "single"],
          tip: "Individual music piece!",
          successTip: "Song, track, or single! All correct! 'Single' = released separately! 🎵"
        }
      ]
    },

    // ===================================================================
    // SET 2: STREAMING & MODERN MUSIC (A2+)
    // ===================================================================
    {
      id: "m3-u9-set2",
      title: "Set 2: Streaming & Digital Music 📱",
      context: `*Martes - Barcelona, España*

Sofia estudia en Barcelona. Ella usa Spotify todos los días.
"Escucho música en Spotify. Creo playlists. Esa canción se volvió viral en TikTok."
Habla de música digital moderna.

*Tuesday - Barcelona, Spain*

Sofia studies in Barcelona. She uses Spotify every day.
"I listen to music on Spotify. I create playlists. That song went viral on TikTok."
She talks about modern digital music.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match platform to its use.",
          pairs: [
            { left: "Spotify 🎧", right: "stream music" },
            { left: "YouTube 📹", right: "watch music videos" },
            { left: "TikTok 📱", right: "short music clips" }
          ],
          a: "completed",
          tip: "Modern platforms!",
          successTip: "Platform uses! Spotify = audio, YouTube = video, TikTok = short clips! 📲"
        },
        {
          type: "mc",
          q: "'I ___ to music on Spotify every day.'",
          options: ["listen", "watch", "see"],
          a: "listen",
          tip: "Audio platform!",
          successTip: "Listen! Spotify = audio streaming service! 🎧"
        },
        {
          type: "input",
          q: "'That song went ___ on TikTok!' (extremely popular)",
          a: "viral",
          alts: [],
          tip: "Internet term for popular!",
          successTip: "Viral! Spread quickly online = went viral! Internet slang! 📈"
        },
        {
          type: "mc",
          q: "'My playlist has ___ 200 songs!'",
          options: ["over", "on", "at"],
          a: "over",
          tip: "More than!",
          successTip: "Over! Over = more than = más de! 'Over 200' = más de 200! 🎵"
        },
        {
          type: "match",
          q: "Match streaming term to meaning.",
          pairs: [
            { left: "playlist 📝", right: "your song collection" },
            { left: "stream 🎧", right: "listen online" },
            { left: "algorithm 🤖", right: "recommends music" }
          ],
          a: "completed",
          tip: "Digital music terms!",
          successTip: "Streaming vocabulary! Modern music consumption! 💻"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Escucho música en Spotify todos los días.'",
          words: ["I", "listen", "to", "music", "on", "Spotify", "every", "day."],
          a: "I listen to music on Spotify every day.",
          extra: ["the", "in", "at"],
          tip: "Listen TO, ON Spotify!"
        },
        {
          type: "mc",
          q: "'I ___ that song to my playlist.' (past)",
          options: ["added", "add", "adding"],
          a: "added",
          tip: "Past action!",
          successTip: "Added! Past simple! Common streaming action! ➕🎵"
        },
        {
          type: "input",
          q: "'This song has over a ___ streams!' (1,000,000,000)",
          a: "billion",
          alts: [],
          tip: "Mil millones!",
          successTip: "Billion! Popular songs get billions of streams! 📊"
        },
        {
          type: "order",
          q: "Traduce: 'Creo playlists para diferentes estados de ánimo.'",
          words: ["I", "create", "playlists", "for", "different", "moods."],
          a: "I create playlists for different moods.",
          extra: ["make", "the", "a"],
          tip: "Moods = estados de ánimo!"
        },
        {
          type: "input",
          q: "'The algorithm ___ me great music.' (suggests)",
          a: "recommends",
          alts: ["suggests"],
          tip: "AI suggestions!",
          successTip: "Recommends! Spotify/YouTube algorithms suggest music based on your taste! 🤖"
        }
      ]
    },

    // ===================================================================
    // SET 3: MUSIC GENRES (A2+)
    // ===================================================================
    {
      id: "m3-u9-set3",
      title: "Set 3: Music Genres 🎸🎹",
      context: `*Miércoles - Buenos Aires, Argentina*

Carlos es fanático de música en Buenos Aires. Hoy habla de géneros.
"Me encanta el rock. También me gusta el tango porque es tradicional argentino."
Comparte sus preferencias de géneros musicales.

*Wednesday - Buenos Aires, Argentina*

Carlos is a music fan in Buenos Aires. Today he talks about genres.
"I love rock. I also like tango because it's traditional Argentine music."
He shares his genre preferences.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match genre to typical description.",
          pairs: [
            { left: "Rock 🎸", right: "loud, guitars, energetic" },
            { left: "Classical 🎻", right: "orchestras, peaceful" },
            { left: "Pop 🎤", right: "catchy, radio-friendly" }
          ],
          a: "completed",
          tip: "Genre characteristics!",
          successTip: "Genre = tipo de música! NOT gender (género masculino/femenino)! 🎵"
        },
        {
          type: "mc",
          q: "Fast rhythm, electronic beats, dance music:",
          options: ["EDM / Electronic", "Classical", "Jazz"],
          a: "EDM / Electronic",
          tip: "Electronic dance!",
          successTip: "EDM! Electronic Dance Music! Popular at festivals! Genre ≠ gender! 🎧"
        },
        {
          type: "input",
          q: "Music genre with rapping and beats = ___ (two words)",
          a: "hip hop",
          alts: ["hip-hop", "rap"],
          tip: "Urban music!",
          successTip: "Hip hop! Urban genre! Hip hop = culture, rap = vocal style! Genre word! 🎤"
        },
        {
          type: "mc",
          q: "Traditional Argentine music with dance:",
          options: ["Tango", "Salsa", "Flamenco"],
          a: "Tango",
          tip: "From Argentina!",
          successTip: "Tango! Traditional Argentine genre! Each country has unique genres! 🇦🇷"
        },
        {
          type: "match",
          q: "Match genre to common instruments.",
          pairs: [
            { left: "Rock", right: "electric guitar, drums" },
            { left: "Jazz", right: "saxophone, trumpet" },
            { left: "Classical", right: "violin, orchestra" }
          ],
          a: "completed",
          tip: "Instruments per genre!",
          successTip: "Genre-instrument match! Each genre has typical instruments! 🎺🎸"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: '¿Qué tipo de música te gusta?'",
          words: ["What", "kind", "of", "music", "do", "you", "like?"],
          a: "What kind of music do you like?",
          extra: ["type", "genre", "are"],
          tip: "Kind = type = género!"
        },
        {
          type: "mc",
          q: "'Are you into jazz?' means:",
          options: ["Do you like jazz?", "Are you inside jazz?", "Do you play jazz?"],
          a: "Do you like jazz?",
          tip: "'Into' = interested in!",
          successTip: "'Into' = like/interested in! Casual question! Genre preference! 🎷"
        },
        {
          type: "input",
          q: "'I like all ___, but I love rock.' (kinds/types)",
          a: "kinds",
          alts: ["types", "genres"],
          tip: "All types of music!",
          successTip: "All kinds! Means you like many genres but prefer one! 🎵"
        },
        {
          type: "order",
          q: "Traduce: 'El reggaeton es popular en América Latina.'",
          words: ["Reggaeton", "is", "popular", "in", "Latin", "America."],
          a: "Reggaeton is popular in Latin America.",
          extra: ["the", "very", "at"],
          tip: "Genre from Latin America!"
        },
        {
          type: "input",
          q: "'I'm not really into heavy metal. It's too ___ for me.'",
          a: "loud",
          alts: ["aggressive", "intense"],
          tip: "Personal preference!",
          successTip: "Too loud! Personal genre preference! Everyone likes different genres! 🎸"
        }
      ]
    },

    // ===================================================================
    // SET 4: DIALOGUE - INTERNATIONAL GROUP CHAT 💬
    // ===================================================================
    {
      id: "m3-u9-set4",
      title: "Set 4: Dialogue - Music Chat 💬",
      context: `*Jueves - Chat internacional*

Maya (India), Sofia (España), Carlos (Argentina), Yuki (Japón) y Ahmed (Egipto) hablan de música en un chat.
"¿Qué música escuchan?" "¿Tienen cantantes favoritos?"
Practican conversaciones sobre música.

*Thursday - International chat*

Maya (India), Sofia (Spain), Carlos (Argentina), Yuki (Japan) and Ahmed (Egypt) talk about music in a chat.
"What music do you listen to?" "Do you have favorite singers?"
They practice music conversations.`,
      
      questions: [
        {
          type: "passage",
          passage: `**Music Group Chat**

Maya: "Hey everyone! What kind of music do you listen to?"
Sofia: "..."
Carlos: "Nice! Why do you like that genre?"
Sofia: "..."`
        },
        {
          type: "input",
          q: "Complete Sofia's answer: 'I listen to ___.' (any genre)",
          a: "pop",
          alts: ["rock", "reggaeton", "electronic", "hip hop", "jazz"],
          tip: "Choose any genre!",
          successTip: "✓ GENRE!\nAny genre works! Personal preference! 🎵"
        },
        {
          type: "mc",
          q: "Complete reason: 'Because it's ___'",
          options: ["catchy and fun", "a music", "in Spanish"],
          a: "catchy and fun",
          tip: "Give adjective reasons!",
          successTip: "✓ ADJECTIVES!\nCatchy, fun = good reasons! Genre characteristics! 🎶"
        },
        {
          type: "passage",
          passage: `**Continuing**

Yuki: "I love K-pop! Do you listen to it?"
Carlos: "..."
Yuki: "You should try it! It's really energetic!"
Ahmed: "I agree! I listen to K-pop sometimes."`
        },
        {
          type: "mc",
          q: "Complete Carlos's answer: '___'",
          options: ["No, I don't listen to it much", "Yes, I love it", "K-pop is Korean"],
          a: "No, I don't listen to it much",
          tip: "Yuki says 'you should try' = he doesn't!",
          successTip: "✓ NEGATIVE!\nNo + don't listen to it! Complete answer! 🎧"
        },
        {
          type: "input",
          q: "Complete Ahmed's frequency: 'I listen to it ___.' (sometimes/often/rarely)",
          a: "sometimes",
          alts: ["often", "occasionally"],
          tip: "How often!",
          successTip: "✓ FREQUENCY!\nSometimes, often, occasionally = all good! ⏰"
        },
        {
          type: "passage",
          passage: `**Music Habits**

Maya: "How often do you listen to music?"
Sofia: "..."
Maya: "Wow! What platform do you use?"
Sofia: "..."`
        },
        {
          type: "input",
          q: "Complete frequency: 'I listen to music ___ ___.' (every day)",
          a: "every day",
          alts: ["all day", "daily"],
          tip: "Frequency expression!",
          successTip: "✓ FREQUENCY!\nEvery day = todos los días! Common answer! 📅"
        },
        {
          type: "mc",
          q: "Complete platform answer: '___'",
          options: ["I use Spotify", "I use music", "I like songs"],
          a: "I use Spotify",
          tip: "Streaming platform!",
          successTip: "✓ PLATFORM!\nSpotify, YouTube Music, Apple Music = streaming services! 📱"
        },
        {
          type: "passage",
          passage: `**Favorite Artists**

Carlos: "Who's your favorite artist?"
Ahmed: "..."
Carlos: "Nice! Do you go to concerts?"
Ahmed: "..."`
        },
        {
          type: "input",
          q: "Complete favorite: 'My favorite artist is ___.' (any artist name or genre)",
          a: "Shakira",
          alts: ["Coldplay", "BTS", "Bad Bunny", "Taylor Swift", "any artist"],
          tip: "Any artist works!",
          successTip: "✓ FAVORITE!\nAny artist name works! Personal preference! ⭐"
        },
        {
          type: "mc",
          q: "Complete concert answer: '___'",
          options: ["Yes, I go to concerts sometimes", "Yes, I listen to music", "Concerts are loud"],
          a: "Yes, I go to concerts sometimes",
          tip: "Do you go? = frequency!",
          successTip: "✓ COMPLETE!\nYes + frequency = full answer! Concert habits! 🎤"
        }
      ]
    },

    // ===================================================================
    // SET 5: MUSIC OPINIONS (A2+) - INTEGRACIÓN UNIT 8
    // ===================================================================
    {
      id: "m3-u9-set5",
      title: "Set 5: Music Opinions 💭",
      context: `*Viernes - Taller de conversación internacional*

Estudiantes de diferentes países comparten opiniones musicales.
"Creo que el jazz es relajante. Encuentro el rock muy emocionante. No me gusta mucho la música clásica."
Expresan opiniones sobre música.

*Friday - International conversation workshop*

Students from different countries share music opinions.
"I think jazz is relaxing. I find rock very exciting. I don't like classical music very much."
They express music opinions.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match opinion expression to register.",
          pairs: [
            { left: "I think...", right: "Casual (everyday)" },
            { left: "In my opinion...", right: "Formal (essays)" },
            { left: "I find...", right: "Sophisticated" }
          ],
          a: "completed",
          tip: "Different formality levels!",
          successTip: "Opinion levels! 'I think' = most common! Integration Unit 8! 💬"
        },
        {
          type: "mc",
          q: "Most natural in conversation: '___ jazz is relaxing.'",
          options: ["I think", "In my opinion", "I believe"],
          a: "I think",
          tip: "Casual opinion!",
          successTip: "I think! Most common! 90% of casual opinions use 'I think'! 🎷"
        },
        {
          type: "input",
          q: "More sophisticated: 'I think rock is exciting' → I ___ rock exciting.",
          a: "find",
          alts: [],
          tip: "I find + object + adjective!",
          successTip: "I find! Sophisticated structure! I find + [genre] + [adjective]! 🎸"
        },
        {
          type: "mc",
          q: "'That song is really ___. I can't get it out of my head!'",
          options: ["catchy", "boring", "awful"],
          a: "catchy",
          tip: "Stuck in your head!",
          successTip: "Catchy! Pegajoso! 'Can't get out of my head' = earworm! 🎵🧠"
        },
        {
          type: "match",
          q: "Match music criticism to meaning.",
          pairs: [
            { left: "overrated 👎", right: "too popular for quality" },
            { left: "underrated 👍", right: "deserves more recognition" },
            { left: "overhyped 📢", right: "too much promotion" }
          ],
          a: "completed",
          tip: "Critical vocabulary!",
          successTip: "Music criticism! Nuanced opinions! Advanced vocabulary! 🎯"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Creo que el reggaeton es energético.'",
          words: ["I", "think", "reggaeton", "is", "energetic."],
          a: "I think reggaeton is energetic.",
          extra: ["the", "very", "so"],
          tip: "I think + opinion!"
        },
        {
          type: "mc",
          q: "More sophisticated: 'I think electronic music is amazing':",
          options: ["I find electronic music amazing", "I find electronic music is amazing", "Electronic music I find amazing"],
          a: "I find electronic music amazing",
          tip: "I find + object + adjective!",
          successTip: "I find... amazing! No 'is' after object! Sophisticated! 🎧"
        },
        {
          type: "input",
          q: "'Their lyrics are so ___. They really make you think.'",
          a: "deep",
          alts: ["meaningful", "powerful", "profound"],
          tip: "Thoughtful music!",
          successTip: "Deep, meaningful, powerful! Lyrics = letras! Music appreciation! ✍️"
        },
        {
          type: "order",
          q: "Traduce: 'Esta banda está sobrevalorada, en mi opinión.'",
          words: ["In", "my", "opinion,", "this", "band", "is", "overrated."],
          a: "In my opinion, this band is overrated.",
          extra: ["the", "very", "think"],
          tip: "Formal opinion starter!"
        },
        {
          type: "input",
          q: "'Their live performances are even ___ than the studio recordings!'",
          a: "better",
          alts: ["more amazing", "more incredible"],
          tip: "Comparative!",
          successTip: "Better! Live > studio! Highest compliment for musicians! 🎤>💿"
        }
      ]
    },

    // ===================================================================
    // SET 6: FREQUENCY + MUSIC HABITS (A2+) - INTEGRACIÓN UNIT 6-7
    // ===================================================================
    {
      id: "m3-u9-set6",
      title: "Set 6: How Often? Music Habits ⏰",
      context: `*Sábado - Tokyo, Japón*

Yuki es estudiante en Tokyo. Hoy habla de sus hábitos musicales.
"Escucho música todos los días. Voy a conciertos dos veces al mes. Creo playlists cada semana."
Comparte su frecuencia musical.

*Saturday - Tokyo, Japan*

Yuki is a student in Tokyo. Today she talks about her music habits.
"I listen to music every day. I go to concerts twice a month. I create playlists every week."
She shares her music frequency.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match frequency expression to meaning.",
          pairs: [
            { left: "every day 📅", right: "all days (100%)" },
            { left: "twice a week 📊", right: "two times per week" },
            { left: "sometimes ⏰", right: "occasionally (30-50%)" }
          ],
          a: "completed",
          tip: "Frequency expressions!",
          successTip: "Frequency from Unit 7! Applied to music habits! Integration! ⏰"
        },
        {
          type: "mc",
          q: "'How often do you listen to music?' - '___'",
          options: ["I listen to music every day", "I like music", "Music is good"],
          a: "I listen to music every day",
          tip: "How often = frequency!",
          successTip: "Every day! 'How often' needs frequency answer! Unit 7 skill! 📅"
        },
        {
          type: "input",
          q: "'I stream music ___ ___ on Spotify.' (every day)",
          a: "every day",
          alts: ["daily", "all day"],
          tip: "Frequency phrase!",
          successTip: "Every day! Daily habit! Frequency + present simple! 🎧"
        },
        {
          type: "mc",
          q: "'I go to concerts ___ a year.' (2 times)",
          options: ["twice", "two", "double"],
          a: "twice",
          tip: "Once, twice, three times!",
          successTip: "Twice! Once = 1x, twice = 2x, three times = 3x! Frequency! 🎤"
        },
        {
          type: "match",
          q: "Match question to frequency answer.",
          pairs: [
            { left: "How often do you listen?", right: "Every day / twice a week" },
            { left: "Do you listen every day?", right: "Yes, I do / No, I don't" },
            { left: "When do you listen?", right: "In the morning / at night" }
          ],
          a: "completed",
          tip: "Different question types!",
          successTip: "Question types! How often, yes/no, when = different answers! ❓"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: '¿Con qué frecuencia escuchas música?'",
          words: ["How", "often", "do", "you", "listen", "to", "music?"],
          a: "How often do you listen to music?",
          extra: ["are", "times", "the"],
          tip: "How often = frequency question!"
        },
        {
          type: "mc",
          q: "Answer: 'How often do you create playlists?' - '___'",
          options: ["I create them once a week", "I like playlists", "Playlists are good"],
          a: "I create them once a week",
          tip: "Frequency answer!",
          successTip: "Once a week! Complete frequency answer! Object pronoun 'them'! 📝"
        },
        {
          type: "input",
          q: "'I go to concerts ___ ___ ___ year.' (three times)",
          a: "three times a",
          alts: ["3 times a"],
          tip: "Number + times + a!",
          successTip: "Three times a year! Frequency pattern! [number] times a [period]! 🎫"
        },
        {
          type: "order",
          q: "Traduce: 'Actualizo mis playlists cada semana.'",
          words: ["I", "update", "my", "playlists", "every", "week."],
          a: "I update my playlists every week.",
          extra: ["the", "a", "all"],
          tip: "Every week = cada semana!"
        },
        {
          type: "input",
          q: "Answer about YOU: 'How often do you listen to music?' → I listen ___.",
          a: "every day",
          alts: ["daily", "often", "sometimes", "rarely", "all the time"],
          tip: "Your real frequency!",
          successTip: "Personal frequency! Any frequency expression works! Integration! ⏰"
        }
      ]
    },

    // ===================================================================
    // SET 7: MUSIC & DAILY ROUTINES (A2+) - INTEGRACIÓN UNIT 6-7
    // ===================================================================
    {
      id: "m3-u9-set7",
      title: "Set 7: Music in Daily Life 🎵⏰",
      context: `*Domingo - Cairo, Egipto*

Ahmed es ingeniero en Cairo. Hoy habla de cuándo escucha música.
"Escucho música cuando viajo al trabajo. Escucho música mientras cocino. No escucho música cuando estudio."
Conecta música con rutinas diarias.

*Sunday - Cairo, Egypt*

Ahmed is an engineer in Cairo. Today he talks about when he listens to music.
"I listen to music when I travel to work. I listen to music while I cook. I don't listen to music when I study."
He connects music with daily routines.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match time expression to example.",
          pairs: [
            { left: "in the morning ☀️", right: "when I wake up" },
            { left: "while cooking 🍳", right: "during meal prep" },
            { left: "on my way to work 🚗", right: "during commute" }
          ],
          a: "completed",
          tip: "When do you listen!",
          successTip: "Time + music! When/while = connect activities! Unit 6-7 routines! ⏰"
        },
        {
          type: "mc",
          q: "'When do you listen to music?' - '___'",
          options: ["I listen in the morning", "I like music", "Every day"],
          a: "I listen in the morning",
          tip: "When = time of day!",
          successTip: "In the morning! 'When' asks time, not frequency! ☀️"
        },
        {
          type: "input",
          q: "'I listen to music ___ I exercise.' (when/while)",
          a: "when",
          alts: ["while"],
          tip: "During exercise!",
          successTip: "When/while! Connects two activities happening together! 🏃🎧"
        },
        {
          type: "mc",
          q: "'I don't listen to music ___ I study because I need concentration.'",
          options: ["when", "where", "who"],
          a: "when",
          tip: "During study time!",
          successTip: "When! 'When I study' = durante estudio! Negative + when! 📚"
        },
        {
          type: "match",
          q: "Match daily routine to music moment.",
          pairs: [
            { left: "Morning routine", right: "listen while getting ready" },
            { left: "Commute", right: "listen on the bus/train" },
            { left: "Evening", right: "listen before bed" }
          ],
          a: "completed",
          tip: "Music + routines!",
          successTip: "Music integration! Part of daily habits! Unit 6-7 connection! 🎵"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Escucho música cuando viajo.'",
          words: ["I", "listen", "to", "music", "when", "I", "travel."],
          a: "I listen to music when I travel.",
          extra: ["while", "the", "on"],
          tip: "When = cuando!"
        },
        {
          type: "mc",
          q: "'I listen to podcasts ___ the morning and music ___ the evening.'",
          options: ["in // in", "at // at", "on // on"],
          a: "in // in",
          tip: "Time of day!",
          successTip: "In! In the morning/evening! Time prepositions from Unit 7! ☀️🌙"
        },
        {
          type: "input",
          q: "'I always listen to music ___ I cook dinner.' (when/while)",
          a: "when",
          alts: ["while"],
          tip: "Durante cooking!",
          successTip: "When/while! Makes cooking more enjoyable! 🍳🎵"
        },
        {
          type: "order",
          q: "Traduce: 'No escucho música cuando trabajo porque me distraigo.'",
          words: ["I", "don't", "listen", "to", "music", "when", "I", "work", "because", "it", "distracts", "me."],
          a: "I don't listen to music when I work because it distracts me.",
          extra: ["the", "while", "at"],
          tip: "Don't + when + because!"
        },
        {
          type: "input",
          q: "Complete about YOU: 'I listen to music when ___.' (your activity)",
          a: "I exercise",
          alts: ["I travel", "I cook", "I clean", "I study", "I wake up", "I shower"],
          tip: "Your real habit!",
          successTip: "Personal routine! Music + daily activities! Integration complete! 🎵⏰"
        }
      ]
    },

    // ===================================================================
    // SET 8: CONCERTS & LIVE MUSIC (A2+)
    // ===================================================================
    {
      id: "m3-u9-set8",
      title: "Set 8: Concerts & Live Music 🎤",
      context: `*Lunes - Rio de Janeiro, Brasil*

Leo es estudiante en Rio. Hoy habla de conciertos.
"Voy a festivales de música. Compro boletos en línea. El público canta junto con el artista."
Describe experiencias de música en vivo.

*Monday - Rio de Janeiro, Brazil*

Leo is a student in Rio. Today he talks about concerts.
"I go to music festivals. I buy tickets online. The audience sings along with the artist."
He describes live music experiences.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match concert term to meaning.",
          pairs: [
            { left: "ticket 🎫", right: "entry to concert" },
            { left: "stage 🎭", right: "where band plays" },
            { left: "audience 👥", right: "people watching" }
          ],
          a: "completed",
          tip: "Concert vocabulary!",
          successTip: "Concert essentials! Ticket, stage, audience = basic concert words! 🎤"
        },
        {
          type: "mc",
          q: "You need a ___ to enter a concert.",
          options: ["ticket", "song", "band"],
          a: "ticket",
          tip: "Entry requirement!",
          successTip: "Ticket! Entrada! Buy tickets online or at the venue! 🎫"
        },
        {
          type: "input",
          q: "Large outdoor music event with multiple bands = music ___",
          a: "festival",
          alts: [],
          tip: "Multiple days, multiple artists!",
          successTip: "Music festival! Coachella, Lollapalooza, Glastonbury! Multiple artists! 🎪"
        },
        {
          type: "mc",
          q: "The people watching the concert:",
          options: ["audience / crowd", "stage", "band"],
          a: "audience / crowd",
          tip: "Público!",
          successTip: "Audience/crowd! Both mean público! Audience = formal, crowd = informal! 👥"
        },
        {
          type: "match",
          q: "Match concert moment to description.",
          pairs: [
            { left: "opening act 🎸", right: "band before main artist" },
            { left: "encore 👏", right: "extra songs at end" },
            { left: "stage dive 🤘", right: "jump into crowd" }
          ],
          a: "completed",
          tip: "Concert culture!",
          successTip: "Concert moments! Fan culture vocabulary! Live music experiences! 🎤"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: '¿Tienes boletos para el concierto?'",
          words: ["Do", "you", "have", "tickets", "for", "the", "concert?"],
          a: "Do you have tickets for the concert?",
          extra: ["get", "buy", "a"],
          tip: "Do you have!"
        },
        {
          type: "mc",
          q: "'The band plays ___ the stage.'",
          options: ["on", "in", "at"],
          a: "on",
          tip: "Preposition!",
          successTip: "On! On the stage! Platform preposition! 🎭"
        },
        {
          type: "input",
          q: "'The crowd always ___ crazy when they play that song!'",
          a: "goes",
          alts: ["gets"],
          tip: "Becomes wild!",
          successTip: "Goes crazy! = se vuelve loco! Concert energy! Present simple for habits! 🎉"
        },
        {
          type: "order",
          q: "Traduce: 'Voy a conciertos dos veces al año.'",
          words: ["I", "go", "to", "concerts", "twice", "a", "year."],
          a: "I go to concerts twice a year.",
          extra: ["the", "in", "times"],
          tip: "Twice a year!"
        },
        {
          type: "input",
          q: "'I bought tickets for the ___ festival next month!' (Rock in Rio, etc.)",
          a: "music",
          alts: ["rock", "jazz", "electronic", "summer"],
          tip: "Any festival type!",
          successTip: "Music festival! Any genre works! Future concert plans! 🎫"
        }
      ]
    },

    // ===================================================================
    // SET 9: LIKES/DISLIKES + BECAUSE (A2+) - INTEGRACIÓN UNIT 8
    // ===================================================================
    {
      id: "m3-u9-set9",
      title: "Set 9: Music Preferences + Reasons 💭",
      context: `*Martes - Taller de expresión musical*

Estudiantes internacionales expresan gustos con razones.
"Me encanta el pop porque es alegre. No me gusta el heavy metal porque es muy ruidoso. Mi género favorito es el jazz."
Usan Unit 8 + vocabulario musical.

*Tuesday - Music expression workshop*

International students express tastes with reasons.
"I love pop because it's cheerful. I don't like heavy metal because it's too loud. My favorite genre is jazz."
They use Unit 8 + music vocabulary.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match feeling to music reason.",
          pairs: [
            { left: "I love jazz because...", right: "it's smooth and relaxing" },
            { left: "I hate opera because...", right: "I find it boring" },
            { left: "I enjoy reggae because...", right: "it has good vibes" }
          ],
          a: "completed",
          tip: "Preference + reason!",
          successTip: "Unit 8 + music! Love/hate + because = complete opinions! Integration! 🎵"
        },
        {
          type: "mc",
          q: "'I love rock because ___'",
          options: ["it's energetic and exciting", "it's a genre", "I listen to it"],
          a: "it's energetic and exciting",
          tip: "Adjective reasons!",
          successTip: "Energetic and exciting! Because + adjectives = good reasons! 🎸"
        },
        {
          type: "input",
          q: "'I can't stand country music because I find ___ too slow.'",
          a: "it",
          alts: [],
          tip: "Object pronoun!",
          successTip: "It! Can't stand from Unit 8! Object pronoun + too + adjective! 🎵"
        },
        {
          type: "mc",
          q: "Complete scale: love, like, don't mind, don't like, ___, ___",
          options: ["hate, can't stand", "dislike, hate", "can't stand, hate"],
          a: "hate, can't stand",
          tip: "Unit 8 preference scale!",
          successTip: "Hate, can't stand! Complete scale from Unit 8! Applied to music! 📊"
        },
        {
          type: "match",
          q: "Match negative feeling to logical music reason.",
          pairs: [
            { left: "I don't like classical", right: "because it's too slow" },
            { left: "I hate heavy metal", right: "because it's too loud" },
            { left: "I can't stand opera", right: "because I find it boring" }
          ],
          a: "completed",
          tip: "Negative + reason!",
          successTip: "Dislikes + reasons! Logical matching! Personal preferences! 🎭"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Me encanta el reggaeton porque es bailable.'",
          words: ["I", "love", "reggaeton", "because", "it's", "danceable."],
          a: "I love reggaeton because it's danceable.",
          extra: ["the", "so", "very"],
          tip: "Love + because!"
        },
        {
          type: "mc",
          q: "'I enjoy listening to jazz, ___ I don't listen to it very often.'",
          options: ["but", "so", "because"],
          a: "but",
          tip: "Contrast!",
          successTip: "But! Enjoy (positive) but don't listen often (contrast)! Unit 8! 🎷"
        },
        {
          type: "input",
          q: "'What's your favorite genre?' - 'My ___ ___ is hip hop.'",
          a: "favorite genre",
          alts: ["favourite genre", "favorite music"],
          tip: "Favorite from Unit 8!",
          successTip: "Favorite genre! Unit 8 + music vocabulary! Integration! ⭐"
        },
        {
          type: "order",
          q: "Traduce: 'No me gusta la música clásica porque la encuentro aburrida.'",
          words: ["I", "don't", "like", "classical", "music", "because", "I", "find", "it", "boring."],
          a: "I don't like classical music because I find it boring.",
          extra: ["the", "very", "so"],
          tip: "Don't like + I find it!"
        },
        {
          type: "input",
          q: "Complete about YOU: 'I love ___ because ___, but I can't stand ___ because ___.'",
          a: "pop it's fun rock it's loud",
          alts: ["jazz it's relaxing metal it's aggressive", "electronic it's energetic classical it's boring"],
          tip: "Your real preferences!",
          successTip: "Personal music opinion! Love + but + can't stand! Unit 8 complete! 🎵"
        }
      ]
    },

    // ===================================================================
    // SET 10: PLAYING INSTRUMENTS (NO CAN) (A2)
    // ===================================================================
    {
      id: "m3-u9-set10",
      title: "Set 10: Musical Instruments 🎸🎹",
      context: `*Miércoles - Escuela de música global*

Estudiantes de todo el mundo hablan de instrumentos.
"Toco la guitarra. Mi hermano toca el piano. Practico todos los días."
Hablan de tocar instrumentos musicales.

*Wednesday - Global music school*

Students from around the world talk about instruments.
"I play guitar. My brother plays piano. I practice every day."
They talk about playing musical instruments.`,
      
      questions: [
        // COMPRENSIÓN (1-5)
        {
          type: "match",
          q: "Match instrument to category.",
          pairs: [
            { left: "guitar 🎸", right: "string instrument" },
            { left: "drums 🥁", right: "percussion" },
            { left: "saxophone 🎷", right: "wind instrument" }
          ],
          a: "completed",
          tip: "Instrument types!",
          successTip: "Instrument categories! String, percussion, wind = main types! 🎶"
        },
        {
          type: "mc",
          q: "'I play ___ guitar.' (article?)",
          options: ["the", "a", "no article"],
          a: "the",
          tip: "Play THE instrument!",
          successTip: "The! Always 'play THE guitar/piano/drums'! Exception: no article for sports! ⚽≠🎸"
        },
        {
          type: "input",
          q: "'She ___ the piano.' (verb = play)",
          a: "plays",
          alts: [],
          tip: "Present simple, she!",
          successTip: "Plays! She plays (third person -s)! Present simple for habits! 🎹"
        },
        {
          type: "mc",
          q: "'I ___ the guitar every day.' (verb for improving skill)",
          options: ["practice", "play", "do"],
          a: "practice",
          tip: "Practice = improve!",
          successTip: "Practice! Practice = practicar para mejorar! Play = just playing! 🎸"
        },
        {
          type: "match",
          q: "Match instrument to typical genre.",
          pairs: [
            { left: "Electric guitar 🎸", right: "Rock, metal" },
            { left: "Saxophone 🎷", right: "Jazz, blues" },
            { left: "Tabla 🪘", right: "Indian music" }
          ],
          a: "completed",
          tip: "Instruments per culture!",
          successTip: "Cultural instruments! Each culture has unique instruments! 🌍"
        },

        // PRODUCCIÓN (6-10)
        {
          type: "order",
          q: "Traduce: 'Toco la guitarra.'",
          words: ["I", "play", "the", "guitar."],
          a: "I play the guitar.",
          extra: ["a", "on", "with"],
          tip: "Play THE instrument!"
        },
        {
          type: "mc",
          q: "'My brother ___ the drums in a band.'",
          options: ["plays", "play", "playing"],
          a: "plays",
          tip: "Third person!",
          successTip: "Plays! He/she plays = third person -s! 🥁"
        },
        {
          type: "input",
          q: "'Do you play any ___?' (instruments - plural)",
          a: "instruments",
          alts: [],
          tip: "General question!",
          successTip: "Instruments! 'Any instruments' = general question! Common! 🎵"
        },
        {
          type: "order",
          q: "Traduce: 'Practico el piano una hora todos los días.'",
          words: ["I", "practice", "the", "piano", "one", "hour", "every", "day."],
          a: "I practice the piano one hour every day.",
          extra: ["a", "for", "in"],
          tip: "Practice + frequency!"
        },
        {
          type: "input",
          q: "Answer about YOU: 'Do you play any instruments?' → Yes, I play ___ / No, I don't play ___.",
          a: "the guitar",
          alts: ["the piano", "the drums", "any instruments", "no instruments"],
          tip: "Your real answer!",
          successTip: "Personal answer! Any instrument OR 'I don't play any' = both valid! 🎸"
        }
      ]
    }
  ],

  // ===================================================================
  // READING SET: MUSIC FESTIVALS AROUND THE WORLD
  // ===================================================================
  readingSet: {
    id: "m3-u9-reading",
    title: "Reading: Music Festivals Around the World 🌍🎪",
    category: "reading",

    questions: [
      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 1: Introduction (preguntas 1-2)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Music Festivals: A Global Celebration**

Music festivals are popular all over the world. People travel to these events to see their favorite artists perform live. Each festival has its own unique style and atmosphere. Let's explore some famous music festivals from different countries.`
      },
      {
        type: "mc",
        q: "According to the text, music festivals are:",
        options: ["Popular worldwide", "Only in America", "Only for rock music"],
        a: "Popular worldwide",
        tip: "All over the world!",
        successTip: "✓ WORLDWIDE!\nFestivals exist globally! Different countries, different styles! 🌍"
      },
      {
        type: "mc",
        q: "People go to festivals to:",
        options: ["See live performances", "Buy instruments", "Learn to sing"],
        a: "See live performances",
        tip: "Main purpose!",
        successTip: "✓ LIVE!\nSee artists perform in person = live performances! 🎤"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 2: Coachella - USA (preguntas 3-4)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Coachella - California, USA**

Coachella is one of the most famous music festivals in the world. It happens every April in the California desert. The festival lasts two weekends and attracts over 100,000 people each day. Coachella features many different music genres including rock, pop, hip hop, and electronic music. People love Coachella not only for the music but also for the art installations and fashion.`
      },
      {
        type: "input",
        q: "When does Coachella happen?",
        a: "April",
        alts: ["every April", "in April"],
        tip: "Month mentioned!",
        successTip: "✓ APRIL!\nEvery April in California desert! 🌵"
      },
      {
        type: "mc",
        q: "How long does Coachella last?",
        options: ["Two weekends", "One day", "One week"],
        a: "Two weekends",
        tip: "Duration in text!",
        successTip: "✓ TWO WEEKENDS!\nTwo weekends = about 6 days total! 🎪"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 3: Glastonbury - UK (preguntas 5-6)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Glastonbury - England, UK**

Glastonbury is the largest outdoor music festival in the UK. It takes place in June on a farm in the English countryside. The festival is famous for its mud! It often rains during Glastonbury, so people wear boots and rain gear. Over 200,000 people attend this festival every year. Glastonbury features rock, pop, electronic, and folk music. The tickets sell out in minutes!`
      },
      {
        type: "mc",
        q: "What is Glastonbury famous for?",
        options: ["Its mud", "Its food", "Its buildings"],
        a: "Its mud",
        tip: "Unique characteristic!",
        successTip: "✓ MUD!\nIt rains → mud! Famous festival characteristic! 🌧️"
      },
      {
        type: "input",
        q: "How many people attend Glastonbury?",
        a: "over 200000",
        alts: ["200000", "over 200,000", "200,000", "more than 200000"],
        tip: "Number in text!",
        successTip: "✓ 200,000!\nOver 200,000 = más de 200,000 personas! Huge! 👥"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 4: Tomorrowland - Belgium (preguntas 7-8)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Tomorrowland - Boom, Belgium**

Tomorrowland is the world's biggest electronic dance music (EDM) festival. It happens every July in Belgium. The festival is famous for its incredible stage designs and special effects. Each year has a different theme, and the organizers create a magical atmosphere. DJs from all over the world perform at Tomorrowland. Tickets are very expensive and difficult to get because the festival is extremely popular.`
      },
      {
        type: "mc",
        q: "What type of music does Tomorrowland feature?",
        options: ["Electronic dance music (EDM)", "Rock", "Classical"],
        a: "Electronic dance music (EDM)",
        tip: "Music genre mentioned!",
        successTip: "✓ EDM!\nElectronic dance music! Specific genre festival! 🎧"
      },
      {
        type: "mc",
        q: "Why are tickets difficult to get?",
        options: ["The festival is very popular", "It's too far", "It's too cold"],
        a: "The festival is very popular",
        tip: "Reason in text!",
        successTip: "✓ POPULAR!\nExtremely popular → tickets sell fast! 🎫"
      },

      // ═══════════════════════════════════════════════════════════════════════
      // BLOQUE 5: Rock in Rio - Brazil (preguntas 9-10)
      // ═══════════════════════════════════════════════════════════════════════
      {
        type: "passage",
        passage: `**Rock in Rio - Rio de Janeiro, Brazil**

Rock in Rio started in 1985 and is now one of the largest music festivals in the world. It happens in Rio de Janeiro, Brazil, but the organizers also create versions in other countries like Portugal and Spain. The festival features rock, pop, metal, and Brazilian music. Rock in Rio is famous for bringing together international superstars and local Brazilian artists. Over 700,000 people attend this week-long celebration of music!`
      },
      {
        type: "input",
        q: "In what year did Rock in Rio start?",
        a: "1985",
        alts: [],
        tip: "Year in text!",
        successTip: "✓ 1985!\nStarted almost 40 years ago! Historic festival! 📅"
      },
      {
        type: "mc",
        q: "Rock in Rio is famous for:",
        options: ["Mixing international and local artists", "Only rock music", "Being cheap"],
        a: "Mixing international and local artists",
        tip: "Special characteristic!",
        successTip: "✓ COMPLETE!\nYou understand music festivals worldwide! International + local = unique! 🌎🎵"
      }
    ]
  },

  // ===================================================================
  // REVIEW SET
  // ===================================================================
  reviewSet: {
    id: "m3-u9-review",
    title: "Unit 9 Review",
    category: "review",
    
    questions: [
      {
        type: "mc",
        q: "Review: Group of musicians:",
        options: ["band", "concert", "song"],
        a: "band",
        tip: "Basic vocabulary!"
      },
      {
        type: "input",
        q: "Review: 'I ___ to music on Spotify.' (present simple)",
        a: "listen",
        alts: [],
        tip: "Listen TO!"
      },
      {
        type: "mc",
        q: "Review: 'That song went ___.' (extremely popular)",
        options: ["viral", "online", "internet"],
        a: "viral",
        tip: "Internet slang!"
      },
      {
        type: "input",
        q: "Review: 'I think jazz is relaxing.' → I ___ jazz relaxing. (sophisticated)",
        a: "find",
        alts: [],
        tip: "I find + object + adjective!"
      },
      {
        type: "mc",
        q: "Review: 'How often do you listen?' → '___'",
        options: ["Every day", "I like music", "Music is good"],
        a: "Every day",
        tip: "Frequency answer!"
      },
      {
        type: "input",
        q: "Review: 'I listen to music ___ I travel.' (when/while)",
        a: "when",
        alts: ["while"],
        tip: "Connect activities!"
      },
      {
        type: "mc",
        q: "Review: Entry to a concert:",
        options: ["ticket", "stage", "band"],
        a: "ticket",
        tip: "Concert vocabulary!"
      },
      {
        type: "input",
        q: "Review: 'I love pop ___ it's fun.' (reason)",
        a: "because",
        alts: [],
        tip: "Because = reason!"
      },
      {
        type: "mc",
        q: "Review: 'I play ___ guitar.'",
        options: ["the", "a", "no article"],
        a: "the",
        tip: "Play THE instrument!"
      },
      {
        type: "mc",
        q: "Review: Complete preference scale from Unit 8: love, like, don't mind, don't like, ___, ___",
        options: ["hate, can't stand", "dislike, hate", "can't stand, hate"],
        a: "hate, can't stand",
        tip: "Unit 8 integration!",
        successTip: "🏆 UNIT 9 COMPLETE! Music vocabulary, preferences, streaming, genres, concerts, and routines mastered! Ready for Unit 10! 🎵🌍"
      }
    ]
  }
};
